/** @jsxImportSource react */
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '../services/api';
import { useTranslation } from '../i18n';
import { useMutation } from '@tanstack/react-query';
import { Loader2, CheckCircle, XCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { toast } from '../components/ui/Toast';

const StatusCard: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode; }> = ({ icon, title, children }) => (
    <div className="relative z-10 w-full max-w-lg mx-auto bg-white p-8 sm:p-12 shadow-2xl rounded-xl border border-secondary-100 animate-fade-in">
        <div className="text-center">
            <div className="flex justify-center mb-6">{icon}</div>
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">{title}</h2>
            <div className="text-secondary-600 space-y-4">{children}</div>
        </div>
    </div>
);

const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const [status, setStatus] = useState('verifying'); // 'verifying', 'form', 'success', 'error'
    const [errorMessage, setErrorMessage] = useState('');
    const [oobCode, setOobCode] = useState<string | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        const code = searchParams.get('oobCode');
        if (code) {
            setOobCode(code);
            api.verifyPasswordResetCode(code)
                .then(() => {
                    setStatus('form');
                })
                .catch(() => {
                    setStatus('error');
                    setErrorMessage(t('invalidOrExpiredLink'));
                });
        } else {
            setStatus('error');
            setErrorMessage(t('invalidLink'));
        }
    }, [searchParams, t]);

    const mutation = useMutation({
        mutationFn: (data: { code: string; newPassword: string }) => api.confirmPasswordReset(data.code, data.newPassword),
        onSuccess: () => {
            setStatus('success');
        },
        onError: () => {
            toast.error(t('unexpectedError'));
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            toast.error(t('passwordMismatch'));
            return;
        }
        if (newPassword.length < 6) {
            toast.error(t('weakPasswordError'));
            return;
        }
        if (oobCode) {
            mutation.mutate({ code: oobCode, newPassword });
        }
    };

    const renderContent = () => {
        switch (status) {
            case 'verifying':
                return (
                    <StatusCard
                        icon={<Loader2 className="w-16 h-16 text-primary-500 animate-spin" />}
                        title={t('verifyingLink')}
                    >
                        <p>{t('pleaseWait')}</p>
                    </StatusCard>
                );
            case 'form':
                return (
                     <div className="relative z-10 w-full max-w-md mx-auto bg-white p-8 sm:p-12 shadow-2xl rounded-xl border border-secondary-100 animate-fade-in">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold text-secondary-900 mb-2">{t('resetYourPassword')}</h2>
                            <p className="text-secondary-500">{t('resetPasswordDesc')}</p>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-secondary-700 text-sm font-medium mb-2" htmlFor="new-password">{t('newPassword')}</label>
                                <div className="relative">
                                    <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                                        <Lock size={18} className="text-secondary-400" />
                                    </div>
                                    <input id="new-password" type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={t('newPasswordPlaceholder')} required className={`w-full py-3 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ps-10 pe-10`} />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)} className={`absolute inset-y-0 end-3 flex items-center`}>
                                        {showPassword ? <EyeOff size={18} className="text-secondary-400" /> : <Eye size={18} className="text-secondary-400" />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-secondary-700 text-sm font-medium mb-2" htmlFor="confirm-password">{t('confirmNewPassword')}</label>
                                 <div className="relative">
                                    <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                                        <Lock size={18} className="text-secondary-400" />
                                    </div>
                                    <input id="confirm-password" type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder={t('newPasswordPlaceholder')} required className={`w-full py-3 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ps-10 pe-10`} />
                                     <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className={`absolute inset-y-0 end-3 flex items-center`}>
                                        {showConfirmPassword ? <EyeOff size={18} className="text-secondary-400" /> : <Eye size={18} className="text-secondary-400" />}
                                    </button>
                                </div>
                            </div>
                            <Button type="submit" fullWidth disabled={mutation.isPending} isLoading={mutation.isPending}>
                                {t('resetPassword')}
                            </Button>
                        </form>
                    </div>
                );
            case 'success':
                return (
                    <StatusCard
                        icon={<CheckCircle className="w-16 h-16 text-emerald-500" />}
                        title={t('passwordResetSuccess')}
                    >
                        <p>{t('passwordResetSuccessDesc')}</p>
                        <Link to="/login">
                            <Button className="mt-6 w-full">{t('goToLogin')}</Button>
                        </Link>
                    </StatusCard>
                );
            case 'error':
                return (
                    <StatusCard
                        icon={<XCircle className="w-16 h-16 text-rose-500" />}
                        title={t('passwordResetFailed')}
                    >
                        <p>{errorMessage}</p>
                        <Link to="/forgot-password">
                            <Button variant="outline" className="mt-6 w-full">{t('tryAgain')}</Button>
                        </Link>
                    </StatusCard>
                );
            default:
                return null;
        }
    };

    return (
        <div className="relative min-h-screen w-full bg-secondary-50 flex items-center justify-center p-4 overflow-hidden">
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
            <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-secondary-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
            {renderContent()}
        </div>
    );
};
export default ResetPasswordPage;